<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'

const { setAnswer, answers } = useSurvey()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const fieldName = computed(() => props.question.id)
const sliderValue = ref<number>(props.question.min || 0)

// Initialisiere sliderValue mit bestehenden Antworten
const initializeValue = () => {
  const existingAnswer = answers.value.find((a) => a.id === props.question.id)
  if (existingAnswer && typeof existingAnswer.answer === 'number') {
    sliderValue.value = existingAnswer.answer
  } else {
    sliderValue.value = props.question.min || 0
  }
}

// Initialisiere beim Mount
initializeValue()

// Watch für Änderungen in answers (z.B. beim Step-Wechsel)
watch(
  () => answers.value,
  () => {
    initializeValue()
  },
  { deep: true },
)

const totalSteps = computed(() => {
  const min = props.question.min || 0
  const max = props.question.max || 100
  const step = props.question.step || 1
  return Math.floor((max - min) / step) + 1
})

const possibleValues = computed(() => {
  const min = props.question.min || 0
  const max = props.question.max || 100
  const step = props.question.step || 1
  const values = []

  for (let i = min; i <= max; i += step) {
    values.push(i)
  }
  return values
})

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const numValue = parseFloat(target.value)
  sliderValue.value = numValue
  setAnswer(props.question.id, props.question.label, numValue, props.answerTarget)
}
</script>

<template>
  <div class="field">
    <div class="mb-1">
      <label :for="props.question.id" class="is-size-7">
        {{ props.question.label }}
        <span v-if="props.question.required" class="has-text-danger">*</span>
      </label>
    </div>

    <!-- Aktueller Wert Display -->
    <div class="slider-value-display">
      <span class="current-value">
        {{ sliderValue }}
        <span v-if="props.question.unit" class="unit">{{ props.question.unit }}</span>
      </span>
    </div>

    <!-- Slider Container -->
    <div class="slider-container">
      <div class="control">
        <Field :name="fieldName" :value="sliderValue" v-slot="{ field, errorMessage }">
          <input
            :id="props.question.id"
            v-bind="field"
            type="range"
            class="slider is-fullwidth"
            :class="{ 'is-danger': errorMessage }"
            :min="props.question.min"
            :max="props.question.max"
            :step="props.question.step || 1"
            :value="sliderValue"
            @input="handleInput"
          />
        </Field>
      </div>

      <!-- Werte-Anzeige unter dem Slider -->
      <div class="slider-labels">
        <span class="label-min">
          {{ props.question.min }}
          <span v-if="props.question.unit" class="unit-small">{{ props.question.unit }}</span>
        </span>
        <span class="label-max">
          {{ props.question.max }}
          <span v-if="props.question.unit" class="unit-small">{{ props.question.unit }}</span>
        </span>
      </div>
    </div>

    <ErrorMessage :name="fieldName" class="help is-danger" />
  </div>
</template>

<style scoped>
@import '../../../assets/main.css';

/* Slider Value Display */
.slider-value-display {
  text-align: right;
  margin-bottom: 1rem;
}

.unit {
  font-size: 1.25rem;
  color: var(--bulma-text-weak, #7a7a7a);
  margin-left: 0.25rem;
}

/* Slider Container */
.slider-container {
  position: relative;
  margin-bottom: 1rem;
}

.step-indicator.is-active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
  font-weight: 600;
}

.slider::-moz-range-thumb:hover {
  transform: scale(1.1);
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

/* Labels unter dem Slider */
.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--bulma-text-weak, #7a7a7a);
}

.unit-small {
  font-size: 0.75rem;
  margin-left: 0.125rem;
}

/* Schritte-Anzeige */
.slider-steps {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 0 0.5rem;
}

.step-indicator {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: var(--bulma-text-weak, #7a7a7a);
  background: var(--bulma-scheme-main-bis, #fafafa);
  border: 1px solid var(--bulma-border, #dbdbdb);
  transition: all 0.2s ease;
}

.current-value {
  font-size: 2rem;
  font-weight: 600;
  color: var(--primary-color);
}

/* Slider Styling */
.slider {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 8px;
  background: var(--bulma-border, #dbdbdb);
  border-radius: 4px;
  outline: none;
  cursor: pointer;
  background-image: linear-gradient(
    to right,
    var(--primary-color) 0%,
    var(--primary-color) var(--progress, 0%),
    var(--bulma-border, #dbdbdb) var(--progress, 0%),
    var(--bulma-border, #dbdbdb) 100%
  );
}

/* Webkit Slider Thumb */
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-color);
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s ease;
}

/* Firefox Slider Thumb */
.slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-color);
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s ease;
}

/* Responsive */
@media (max-width: 768px) {
  .current-value {
    font-size: 1.75rem;
  }

  .slider-steps {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .step-indicator {
    font-size: 0.75rem;
    padding: 0.125rem 0.25rem;
  }
}
</style>
